// v1.2.0 — the secret that controls an M® Note: its two written forms.
//
//   lock code — 11 Crockford symbols + 1 check (55 bits), shown 7KQ2-VN8X-P4DA,
//               said aloud or sent another way; typed on the Take page.
//   cash link — 16 Crockford symbols (80 bits) in the link (a path segment since app 1.4.46, after `#` before); nobody
//               types it. "Anyone with the link."
//
// This file is the LIGHT half: making, formatting and reading the symbols. It
// is safe on the main thread and in the main bundle. The heavy half — turning
// a secret into a key with argon2id — is lockcode.ts, and only kdf.worker.ts
// (or kdf.ts's fallback) may import that: a static import of lockcode.ts from
// anything the home screen loads would pull the wasm KDF into the main bundle.

import { bytesToHex, type Hex } from "viem";
import { CROCKFORD, luhn32, luhn32Valid, normalizeSymbols } from "./serial";

export const CODE_DATA_LEN = 11;
export const CODE_LEN = 12; // + check
export const LINK_SECRET_LEN = 16;

/** Uniform random Crockford symbols (256 is a multiple of 32, so a byte & 31 is unbiased). */
function randomSymbols(n: number): string {
  const b = crypto.getRandomValues(new Uint8Array(n));
  let s = "";
  for (let i = 0; i < n; i++) s += CROCKFORD[b[i] & 31];
  return s;
}

/** A fresh lock code: 11 random symbols + the check symbol. */
export function newCode(): string {
  const data = randomSymbols(CODE_DATA_LEN);
  return data + luhn32(data);
}

/** A fresh cash-link secret: 16 random symbols, no check (nobody types it). */
export function newLinkSecret(): string {
  return randomSymbols(LINK_SECRET_LEN);
}

/** A fresh 32-byte salt for issueNote. Public; the contract keeps it with the note. */
export function newSalt(): Hex {
  return bytesToHex(crypto.getRandomValues(new Uint8Array(32)));
}

/** "7KQ2-VN8X-P4DA" */
export function formatCode(code: string): string {
  const c = normalizeSymbols(code);
  return `${c.slice(0, 4)}-${c.slice(4, 8)}-${c.slice(8, 12)}`;
}

/**
 * What a person typed → the 12 normalised symbols, or null when the length or
 * the check symbol is wrong. (The check says *something* is wrong, not where.)
 */
export function parseCode(input: string): string | null {
  const c = normalizeSymbols(input);
  if (c.length !== CODE_LEN || !luhn32Valid(c)) return null;
  return c;
}

/** The secret a code stands for: its 11 data symbols (the check is not part of the key). */
export function secretFromCode(code: string): string {
  return normalizeSymbols(code).slice(0, CODE_DATA_LEN);
}

/** The secret in a link fragment, or null. */
export function secretFromFragment(hash: string): string | null {
  const s = normalizeSymbols(hash.replace(/^#/, ""));
  return s.length === LINK_SECRET_LEN ? s : null;
}
